/**
 * Errores OIDC que NO son fallos reales.
 *
 * Cuando la aplicacion arranca intenta una autenticacion silenciosa para
 * aprovechar una sesion SSO existente. Si no la hay, Keycloak responde
 * `login_required`: es la respuesta correcta y esperada, no una averia.
 * Mostrarla como error alarma al usuario sin motivo.
 */
const SILENT_PROBE_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
] as const;

/**
 * `oidc-client-ts` envuelve la respuesta de Keycloak en un `ErrorResponse`,
 * de modo que el codigo puede llegar como propiedad (`error.error`) o dentro
 * del mensaje ("ErrorResponse: login_required"). Se comprueban ambos.
 */
export function isSilentProbeError(error: unknown): boolean {
  if (!error) return false;

  const code = (error as { error?: string }).error?.trim();
  if (code && SILENT_PROBE_ERRORS.includes(code as (typeof SILENT_PROBE_ERRORS)[number])) {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error);
  return SILENT_PROBE_ERRORS.some((known) =>
    new RegExp(`\\b${known}\\b`).test(message),
  );
}

/**
 * Senales de que la SESION caduco, no de que Keycloak este roto.
 *
 * Son dos cosas muy distintas que llegan por el mismo sitio. Cuando el token
 * esta a punto de expirar, `automaticSilentRenew` presenta el refresh token;
 * si la sesion SSO ya murio —por inactividad, o porque Keycloak se reinicio—
 * la respuesta es `400 invalid_grant` con «Session not active» dentro.
 *
 * Durante un tiempo eso acababa en la pantalla roja de «No se pudo iniciar
 * sesion. Comprueba que Keycloak este disponible», que es falso: Keycloak
 * estaba perfectamente y respondia justo lo que tenia que responder. El
 * usuario pulsaba «Reintentar» y entraba, porque reintentar era exactamente
 * lo que hacia falta. Esta lista existe para que lo haga la aplicacion.
 *
 * `invalid_grant` es el codigo de OAuth; los textos son los que pone Keycloak
 * y se comprueban aparte porque el codigo no siempre viaja suelto.
 */
const EXPIRED_SESSION_ERRORS = ['invalid_grant'] as const;

const EXPIRED_SESSION_TEXTS = [
  'session not active',
  'token is not active',
  "session doesn't have required client",
] as const;

export function isExpiredSessionError(error: unknown): boolean {
  if (!error) return false;

  const code = (error as { error?: string }).error?.trim();
  if (code && EXPIRED_SESSION_ERRORS.includes(code as (typeof EXPIRED_SESSION_ERRORS)[number])) {
    return true;
  }

  /*
   * Busqueda por subcadena y no por limite de palabra, al reves que arriba.
   * `invalid_grant` y los textos de Keycloak son lo bastante distintivos como
   * para que no haya falsos positivos, y asi no hay nada que escapar: dentro
   * de una plantilla, un limite de palabra mal escrito se convierte en un
   * caracter de retroceso y el filtro deja de reconocer justo lo que vino a
   * reconocer.
   */
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return [...EXPIRED_SESSION_ERRORS, ...EXPIRED_SESSION_TEXTS].some((senal) =>
    message.includes(senal),
  );
}

/**
 * Un error que si merece mostrarse al usuario (Keycloak caido, red, etc.).
 *
 * Ni los sondeos silenciosos ni las sesiones caducadas entran aqui: los
 * primeros son la respuesta normal a «¿hay sesion?» y las segundas se
 * resuelven volviendo a Keycloak, no ensenando una alarma.
 */
export function realAuthError(error: unknown): Error | null {
  if (!error || isSilentProbeError(error) || isExpiredSessionError(error)) return null;
  return error instanceof Error ? error : new Error(String(error));
}
